<template>
  <div class="login-screen">
    <div class="login-card">
      <h1 class="login-title">CONFIRMA TU CORREO</h1>

      <!-- Confirmando -->
      <template v-if="checking">
        <p class="login-sub">Confirmando tu cuenta…</p>
      </template>

      <!-- Cuenta confirmada -->
      <template v-else-if="done">
        <div class="login-success">{{ successMessage }}</div>
        <a href="/login/" class="login-btn login-btn-link">IR A INICIAR SESIÓN</a>
      </template>

      <!-- Enlace inválido o vencido: se ofrece uno nuevo -->
      <template v-else>
        <div class="login-error">{{ errorMessage }}</div>

        <template v-if="resent">
          <div class="login-success">{{ resentMessage }}</div>
        </template>
        <template v-else>
          <p class="login-sub">
            Escribe tu correo y te enviamos un enlace nuevo.
          </p>
          <form class="login-form" @submit.prevent="handleResend">
            <input
              v-model="email"
              class="login-input"
              type="email"
              placeholder="Email"
              autocomplete="email"
            />
            <button type="submit" class="login-btn" :disabled="resending">
              {{ resending ? 'ENVIANDO…' : 'REENVIAR CORREO' }}
            </button>
          </form>
        </template>

        <a href="/login/" class="login-forgot">← Volver a iniciar sesión</a>
      </template>
    </div>
  </div>
</template>

<script>
import { confirmEmail, resendVerificationEmail } from "@/services/teatroApi";

export default {
  name: "EmailConfirm",
  data() {
    return {
      token: "",
      checking: true,
      done: false,
      errorMessage: "",
      successMessage: "",
      email: "",
      resending: false,
      resent: false,
      resentMessage: "",
    };
  },
  async created() {
    this.parseUrl();
    await this.confirm();
  },
  methods: {
    parseUrl() {
      // La URL es /confirmar-correo/<token>/
      const parts = window.location.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("confirmar-correo");
      if (idx !== -1) this.token = parts[idx + 1] || "";
    },
    async confirm() {
      this.checking = true;
      try {
        if (!this.token) {
          this.errorMessage = "El enlace no es válido. Pide uno nuevo.";
          return;
        }
        const data = await confirmEmail(this.token);
        this.done = true;
        this.successMessage =
          data.message || "Tu correo quedó confirmado. Ya puedes iniciar sesión.";
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error ||
          "No pudimos confirmar tu correo. Pide un enlace nuevo.";
        console.error(error);
      } finally {
        this.checking = false;
      }
    },
    async handleResend() {
      if (!this.email) {
        this.errorMessage = "Ingresa tu correo electrónico.";
        return;
      }
      this.resending = true;
      try {
        const data = await resendVerificationEmail(this.email);
        this.resent = true;
        this.resentMessage =
          data.message ||
          "Si tu cuenta está pendiente de confirmación, te enviamos un correo nuevo.";
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || "No pudimos enviar el correo.";
      } finally {
        this.resending = false;
      }
    },
  },
};
</script>

<style scoped>
.login-screen {
  min-height: calc(100vh - 40px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

.login-card {
  width: 100%;
  max-width: 420px;
  background: rgba(10, 6, 6, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  padding: 40px 38px 34px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  text-align: center;
  backdrop-filter: blur(2px);
}

.login-title {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 2px;
  margin: 0 0 8px;
}

.login-sub {
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  letter-spacing: 0.4px;
  margin: 0 0 26px;
}

.login-error {
  background: rgba(200, 16, 46, 0.15);
  border: 1px solid rgba(200, 16, 46, 0.5);
  color: #ffb3b3;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 16px;
}

.login-success {
  background: rgba(46, 160, 87, 0.15);
  border: 1px solid rgba(46, 160, 87, 0.5);
  color: #a7e8bf;
  border-radius: 8px;
  padding: 11px 14px;
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 16px;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.login-input {
  width: 100%;
  background: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px 16px;
  font-family: var(--font-family-primary);
  font-size: 13px;
  color: #1a1a1a;
  outline: none;
}

.login-input::placeholder {
  color: #9a9a9a;
}

.login-btn {
  margin-top: 6px;
  width: 100%;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px;
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.5);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

.login-btn-link {
  display: inline-block;
  text-decoration: none;
  text-align: center;
}

.login-forgot {
  display: inline-block;
  margin-top: 18px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.login-forgot:hover {
  color: #fff;
}
</style>
